import React, { useState } from "react";
import {
  notification,
  getDBFormatedDate,
  getLoggedInUserTimezone,
  computeBookingEndSlot,
} from "../../../utils/utility";
import Loader from "../../../components/Loader/Loader";
import { Modal, ModalBody } from "reactstrap";
import Calendar from "react-calendar";
import "react-calendar/dist/Calendar.css";
import { getAdminBusinessId } from "../../../utils/authHelper";
import { getAvailableTimeSlots } from "../../../actions/bookingAction";
import { rescheduleEvent } from "../../../actions/admin/scheduled-event-actions";
import { connect } from "react-redux";
import isEmpty from "../../../validation/is-empty";
import moment from "moment";

const getRescheduleTimezone = (data) => {
  if (data && !isEmpty(data.invitee_timezone)) {
    return data.invitee_timezone;
  }
  return getLoggedInUserTimezone();
};

const RescheduleModal = (props) => {
  const [calendarDate, setCalendarDate] = useState(new Date());
  const [activeStartDate, setActiveStartDate] = useState(new Date());
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [slots, setSlots] = useState([]);
  const [dateSelected, setDateSelected] = useState(false);
  const [isLoadingSlots, setIsLoadingSlots] = useState(false);
  const [isConfirming, setIsConfirming] = useState(false);
  const [bookingDate, setBookingDate] = useState(null);

  const { data } = props;
  const timezone = getRescheduleTimezone(data);
  const staffId =
    data && data.staff_assignment_type == "2" && !isEmpty(data.staff_id)
      ? data.staff_id
      : null;


  // Invitee display name (first name only for brevity)
  const inviteeShort = data?.invitee_name
    ? data.invitee_name.split(" ")[0]
    : "";

  const onCalendarDateChange = (date) => {
    const { getAvailableTimeSlots } = props;
    setSelectedSlot(null);
    setSlots([]);
    setCalendarDate(date);
    setDateSelected(true);
    setBookingDate(getDBFormatedDate(new Date(date)));
    setIsLoadingSlots(true);
    getAvailableTimeSlots({
      data: {
        selected_date: getDBFormatedDate(new Date(date)),
        staff_id: staffId,
        business_id: getAdminBusinessId(),
        event_id: data.event_id,
        timezone,
      },
      onSuccess: (response) => {
        setIsLoadingSlots(false);
        setSlots(response);
      },
      onError: (error) => {
        setIsLoadingSlots(false);
        notification.error(
          error?.response?.data?.message || "Could not load available times."
        );
      },
    });
  };

  const onSelectTimeSlot = (slot) => setSelectedSlot(slot);

  const onConfirm = () => {
    const { rescheduleEvent, handleClose, handleViewDetailClose, loadBookings } = props;
    const bookingDateTime = bookingDate + " " + selectedSlot;
    const endSlot = computeBookingEndSlot(bookingDate, selectedSlot, data.timeslot, timezone);

    setIsConfirming(true);
    rescheduleEvent({
      data: {
        booking_id: data.id,
        booking_date_time: bookingDateTime,
        start_time: selectedSlot + ":00",
        end_time: endSlot + ":00",
        reschedule_reason: "",
        timezone,
      },
      onSuccess: () => {
        setIsConfirming(false);
        notification.success("Rescheduled successfully.");
        handleClose();
        handleViewDetailClose();
        loadBookings();
      },
      onError: (error) => {
        setIsConfirming(false);
        notification.error(
          !isEmpty(error?.response?.data?.message)
            ? error.response.data.message
            : "An error occurred. Please try again."
        );
      },
    });
  };

  const goToNextMonth = () => {
    const next = moment(activeStartDate).add(1, "month").toDate();
    setActiveStartDate(next);
  };

  // The API returns slot objects: { slot: "HH:mm", status: "NA" | available }.
  // Normalize to plain "HH:mm" strings and drop unavailable ("NA") slots.
  const availableSlots = (slots || [])
    .map((s) => (typeof s === "string" ? { slot: s, status: "OK" } : s))
    .filter((s) => s && s.slot && s.status !== "NA")
    .map((s) => s.slot);

  // Render a friendly 12h label but keep the raw "HH:mm" value for the API.
  const formatSlotLabel = (slot) => {
    const m = moment(slot, ["HH:mm", "H:mm", "h:mm A", "hh:mm A"], true);
    return m.isValid() ? m.format("h:mm A") : slot;
  };

  return (
    <>
      <Loader isShowLoader={isConfirming} />
      <Modal
        isOpen={props.showRescheduleModal}
        toggle={props.handleClose}
        centered
        className="mc-reschedule-modal"
        size="lg"
      >
        <ModalBody className="p-0">
          {/* ── Header ─────────────────────────────────────────── */}
          <div className="mc-rs__header">
            <div>
              <h2 className="mc-rs__title">Reschedule booking</h2>
              <p className="mc-rs__subtitle">Choose a new date and time for this booking.</p>
            </div>
            <button className="mc-rs__close" onClick={props.handleClose} aria-label="Close">×</button>
          </div>

          {/* ── Event info bar ──────────────────────────────────── */}
          {data && (
            <div className="mc-rs__info-bar">
              <span className="mc-rs__info-event">
                <strong>{data.event_name}</strong>
                {inviteeShort && <> · {inviteeShort}</>}
              </span>
            </div>
          )}

          {/* ── Body: date + time columns ───────────────────────── */}
          <div className="mc-rs__body">
            {/* Left: date picker */}
            <div className="mc-rs__col mc-rs__col--date">
              <p className="mc-rs__col-label">
                <span className="mc-rs__col-num">1.</span> Choose a date
              </p>
              <div className="mc-rs__calendar-wrap">
                <Calendar
                  minDate={new Date()}
                  onChange={onCalendarDateChange}
                  value={calendarDate}
                  activeStartDate={activeStartDate}
                  onActiveStartDateChange={({ activeStartDate }) =>
                    setActiveStartDate(activeStartDate)
                  }
                  locale="en-GB"
                  prev2Label={null}
                  next2Label={null}
                  prevLabel={
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="15 18 9 12 15 6"/>
                    </svg>
                  }
                  nextLabel={
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="9 18 15 12 9 6"/>
                    </svg>
                  }
                />
              </div>
              {/* No dates available hint */}
              {dateSelected && !isLoadingSlots && availableSlots.length === 0 && (
                <div className="mc-rs__no-dates">
                  <span>No available times in {moment(activeStartDate).format("MMMM")}.</span>
                  <button className="mc-rs__next-month-link" onClick={goToNextMonth}>
                    See next available month →
                  </button>
                </div>
              )}
            </div>

            {/* Right: time slots */}
            <div className="mc-rs__col mc-rs__col--time">
              <p className="mc-rs__col-label">
                <span className="mc-rs__col-num">2.</span> Choose a time
              </p>
              <div className="mc-rs__slots-box">
                {!dateSelected && (
                  <div className="mc-rs__empty-state">
                    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#c8ccd4" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
                    </svg>
                    <strong>Select a date first</strong>
                    <span>Available times will appear here.</span>
                  </div>
                )}

                {dateSelected && isLoadingSlots && (
                  <div className="mc-rs__empty-state">
                    <div className="mc-rs__spinner" />
                    <span>Loading available times…</span>
                  </div>
                )}

                {dateSelected && !isLoadingSlots && availableSlots.length === 0 && (
                  <div className="mc-rs__empty-state">
                    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#c8ccd4" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/>
                    </svg>
                    <strong>No available times</strong>
                    <span>Try selecting a different date.</span>
                  </div>
                )}

                {dateSelected && !isLoadingSlots && availableSlots.length > 0 && (
                  <div className="mc-rs__slots-grid">
                    {availableSlots.map((slot, index) => (
                      <button
                        key={index}
                        className={`mc-rs__slot-btn ${selectedSlot === slot ? "mc-rs__slot-btn--active" : ""}`}
                        onClick={() => onSelectTimeSlot(slot)}
                      >
                        {formatSlotLabel(slot)}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* ── Footer ──────────────────────────────────────────── */}
          <div className="mc-rs__footer">
            <button className="mc-rs__btn mc-rs__btn--cancel" onClick={props.handleClose}>
              Cancel
            </button>
            <button
              className="mc-rs__btn mc-rs__btn--confirm"
              disabled={!selectedSlot || isConfirming}
              onClick={onConfirm}
            >
              {isConfirming ? "Confirming…" : "Confirm new time"}
            </button>
          </div>
        </ModalBody>
      </Modal>
    </>
  );
};

export default connect(null, {
  getAvailableTimeSlots,
  rescheduleEvent,
})(RescheduleModal);
